/**
 * What changed since an earlier review. The earlier figures are recomputed
 * from the documents that had been read by the time of that review, so every
 * past review is a usable baseline without storing its numbers. Corrections
 * and rules made since apply to both sides, so the comparison shows new data,
 * not reclassification. Recommendations are compared through the
 * `recs_json` each review stores (from 2026-10-23 on). See ARCHITECTURE.md,
 * What changed.
 */
import { analyse, GROUP_LABEL, type Group, type Snapshot } from './analysis'
import type { Tables } from './book'
import type { Row } from './google/sheets'

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))

/** The tables as they stood at `runAt`: only rows from documents read by then. */
export function tablesAt(tables: Tables, runAt: string): Tables {
  const read = new Set(tables.documents.filter((d) => str(d.processed_at) && str(d.processed_at) <= runAt).map((d) => str(d.file_id)))
  const keep = (r: Row) => !str(r.source_file_id) || read.has(str(r.source_file_id))
  const day = runAt.slice(0, 10)
  return {
    ...tables,
    // An account closed after the review was still open when it ran.
    accounts: tables.accounts.map((a) => (str(a.closed) > day ? { ...a, closed: null } : a)),
    documents: tables.documents.filter((d) => read.has(str(d.file_id))),
    balances: tables.balances.filter(keep),
    holdings: tables.holdings.filter(keep),
    transactions: tables.transactions.filter(keep),
  }
}

/** Documents read after `runAt`. */
export function documentsSince(tables: Tables, runAt: string): Row[] {
  return tables.documents
    .filter((d) => str(d.processed_at) > runAt && ['done', 'review'].includes(str(d.status)))
    .sort((a, b) => str(a.period_end).localeCompare(str(b.period_end)))
}

export type Format = 'money' | 'percent' | 'months' | 'count'

export interface Measure {
  label: string
  then: number | null
  now: number | null
  format: Format
  /** True when a rise is good news; the arrow word says which way it went either way. */
  higherIsBetter: boolean
}

export interface Comparison {
  baseline: string
  measures: Measure[]
  groups: Measure[]
  /** Percentage-point change in each asset class's share. */
  allocation: { label: string; then: number; now: number }[]
  documents: Row[]
}

export function compare(tables: Tables, now: Snapshot, baseline: string): Comparison {
  const then = analyse(tablesAt(tables, baseline))
  const m = (label: string, f: (s: Snapshot) => number | null, format: Format, higherIsBetter = true): Measure => ({
    label,
    then: f(then),
    now: f(now),
    format,
    higherIsBetter,
  })
  const groupNames = [...new Set([...then.groups, ...now.groups].map((g) => g.group))]
  const total = (s: Snapshot, g: Group) => s.groups.find((x) => x.group === g)?.total ?? 0
  const classes = [...new Set([...then.investments.allocation, ...now.investments.allocation].map((a) => a.label))]
  const share = (s: Snapshot, c: string) => s.investments.allocation.find((a) => a.label === c)?.share ?? 0
  return {
    baseline,
    measures: [
      m('Net worth', (s) => s.totals.net, 'money'),
      m('Assets', (s) => s.totals.assets, 'money'),
      m('Owed', (s) => s.totals.debts, 'money', false),
      m('Cash', (s) => s.totals.cash, 'money'),
      m('Invested', (s) => s.totals.invested, 'money'),
      m('Savings rate', (s) => s.cash_flow.savings_rate, 'percent'),
      m('Spending a month', (s) => s.cash_flow.avg_monthly_spending, 'money', false),
      m('Emergency fund', (s) => s.cash_flow.emergency_fund_months, 'months'),
      m('Statements read', (s) => s.documents, 'count'),
    ],
    groups: groupNames.map((g) => m(GROUP_LABEL[g], (s) => total(s, g), 'money', g !== 'debt')),
    allocation: classes
      .map((c) => ({ label: c, then: share(then, c), now: share(now, c) }))
      .filter((a) => Math.abs(a.now - a.then) >= 0.005),
    documents: documentsSince(tables, baseline),
  }
}

export interface ReviewRec {
  title: string
  priority: string
  continues: string | null
}

export function reviewRecs(analysis: Row | undefined): ReviewRec[] | undefined {
  if (!analysis || !str(analysis.recs_json)) return undefined
  try {
    return JSON.parse(str(analysis.recs_json)) as ReviewRec[]
  } catch {
    return undefined
  }
}

export interface RecChanges {
  added: ReviewRec[]
  kept: ReviewRec[]
  /** Earlier recommendations the latest review no longer makes. */
  dropped: ReviewRec[]
}

/** The latest review's recommendations against the one before; undefined until both recorded theirs. */
export function recChanges(analyses: Row[]): RecChanges | undefined {
  const latest = reviewRecs(analyses.at(-1))
  const before = reviewRecs(analyses.at(-2))
  if (!latest || !before) return undefined
  const continued = new Set(latest.map((r) => r.continues).filter(Boolean))
  return {
    added: latest.filter((r) => !r.continues),
    kept: latest.filter((r) => r.continues),
    dropped: before.filter((r) => !continued.has(r.title)),
  }
}

/**
 * The review to compare with by default: the latest one that saw fewer
 * documents than are on file now, else the one before the latest.
 */
export function defaultBaseline(tables: Tables, analyses: Row[]): string | undefined {
  const runs = analyses.map((a) => str(a.run_at)).filter(Boolean)
  const differs = [...runs].reverse().find((r) => documentsSince(tables, r).length > 0)
  return differs ?? runs.at(-2)
}
